import { Component, type ErrorInfo, type ReactNode } from "react";
import { BrandScreen } from "./BrandScreen.js";
import { Button, t } from "@mmm/ui";
interface AppErrorBoundaryProps {
  children: ReactNode;
}

interface AppErrorBoundaryState {
  error: Error | null;
}

export class AppErrorBoundary extends Component<AppErrorBoundaryProps, AppErrorBoundaryState> {
  state: AppErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): AppErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("App failed to render", error, errorInfo);
  }

  render() {
    if (!this.state.error) return this.props.children;

    // This boundary wraps App (and therefore LanguageProvider), so it can
    // render before the catalog has loaded — t() then falls back to the raw
    // key. Accepted: a pre-boot crash screen with keys still beats an
    // untranslatable hard-coded one, and a post-boot crash (the common case)
    // renders fully translated. Store t (not useT) because this is a class
    // component and the error state is terminal anyway.
    return (
      <BrandScreen className="mmm-auth-screen" role="alert" aria-label={t("errors.appFailedToLoad")}>
        <div className="mmm-brand-cue">
          <div className="mmm-auth-actions" aria-label={t("errors.recoveryActions")}>
            <Button variant="primary" onClick={reloadApp}>
              {t("common.reload")}
            </Button>
            <Button variant="secondary" onClick={resetStoredSession}>
              {t("auth.resetSignIn")}
            </Button>
          </div>
        </div>
      </BrandScreen>
    );
  }
}

function reloadApp() {
  window.location.reload();
}

function resetStoredSession() {
  try {
    localStorage.clear();
    sessionStorage.clear();
  } catch {
    // Ignore storage failures; navigating to /login still gives the app a
    // clean route and a visible recovery path.
  }
  window.location.assign("/login");
}
